import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Formik } from 'formik'
import { Button, Card, Grid, makeStyles, Typography } from '@material-ui/core'
import {
  getSponsorshipLevelById,
  resetSponsorshipLevel,
  addSponsorshipLevel,
  updateSponsorshipLevel
} from 'store/sponsorship-level'
import { sponsorshipLevelFormSchema } from 'schemas/sponsorshipLevel'
import { toast } from 'utils/notify'
import ViewHeader from 'components/ViewHeader'
import ActionBar from 'components/ActionBar'
import CustomTextField from 'components/Form/CustomTextField'
import ProgressButton from 'components/ProgressButton'
import CustomCheckboxField from 'components/Form/CustomCheckboxField'
import { Box } from '@mui/material'
import DataLoading from 'components/DataLoading'

const SponsorshipLevelsForm = ({ displayContext, onCloseModal, onSuccess }) => {
  const classes = useStyles()
  const { id } = useParams()
  const history = useHistory()
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)
  const [performingAction, setPerformingAction] = useState(false)

  const { user } = useSelector((state) => state.auth)
  const { sponsorshipLevel } = useSelector((state) => state.sponsorshipLevel)

  let initialValues = {
    tierName: sponsorshipLevel?.tierName || '',
    sponsorshipTier: sponsorshipLevel?.sponsorshipTier || '',
    allowLogo: sponsorshipLevel?.allowLogo || false,
    allowAdBanner: sponsorshipLevel?.allowAdBanner || false,
    allowBrochure: sponsorshipLevel?.allowBrochure || false
  }

  useEffect(() => {
    const fetch = async (id) => {
      setIsLoading(true)
      await dispatch(getSponsorshipLevelById(id))
      setIsLoading(false)
    }

    if (id) {
      fetch(id)
    }
    return () => dispatch(resetSponsorshipLevel())
  }, [id, dispatch])

  const onSubmit = async (values) => {
    try {
      setPerformingAction(true)
      const payload = {
        tierName: values.tierName.trim(),
        sponsorshipTier: values.sponsorshipTier,
        allowLogo: values.allowLogo,
        allowAdBanner: values.allowAdBanner,
        allowBrochure: values.allowBrochure
      }

      let res, message
      if (id) {
        res = await updateSponsorshipLevel(payload, id, user?.conferenceId)
        message = 'Sponsorship level updated successfully'
      } else {
        res = await addSponsorshipLevel(payload, user?.conferenceId)
        message = 'Sponsorship level created successfully'
      }

      if (res && displayContext === 'page') {
        toast.success(message)
        goBack()
      }

      displayContext === 'modal' && res && onSuccess && onSuccess(res)
    } catch (err) {
      // do nothing
    } finally {
      setPerformingAction(false)
    }
  }

  const onCancel = () => {
    if (displayContext === 'page') {
      goBack()
    } else {
      onCloseModal()
    }
  }

  const goBack = () => {
    dispatch(resetSponsorshipLevel())
    history.goBack()
  }

  if (isLoading) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <DataLoading />
      </div>
    )
  }

  return (
    <Card
      elevation={displayContext === 'page' ? 3 : 0}
      className={displayContext === 'page' ? classes.card : classes.styledCard}>
      {displayContext === 'page' && (
        <ViewHeader title={id ? 'Edit sponsorship level' : 'Create sponsorship level'}>
          <ActionBar actionText="Back" onAction={goBack} />
        </ViewHeader>
      )}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={sponsorshipLevelFormSchema}>
        <Form>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label="Tier Name"
                name="tierName"
                type="text"
                disabled={sponsorshipLevel?.sponsorshipTier === 1}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label="Sponsorship Tier"
                name="sponsorshipTier"
                type="number"
                disabled={sponsorshipLevel?.sponsorshipTier === 1}
              />
            </Grid>
          </Grid>

          <Box mt={3} mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              File Upload Permissions
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Control which files organizations with this tier are allowed to upload
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <CustomCheckboxField name="allowLogo" label="Allow uploading Logo?" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CustomCheckboxField name="allowAdBanner" label="Allow uploading Ad Banner?" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CustomCheckboxField name="allowBrochure" label="Allow uploading Brochure?" />
            </Grid>
          </Grid>

          <Box className={classes.footerButtons}>
            <Button size="large" onClick={onCancel} disabled={performingAction}>
              Cancel
            </Button>
            <ProgressButton
              type="submit"
              label="Save"
              inProgress={performingAction}
              disabled={performingAction}
            />
          </Box>
        </Form>
      </Formik>
    </Card>
  )
}

export default SponsorshipLevelsForm

const useStyles = makeStyles({
  footerButtons: {
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '0.75rem'
  },
  card: {
    padding: '24px',
    overflow: 'visible'
  },
  styledCard: {
    paddingTop: '1rem'
  }
})
